// Where progress lives when it is not in this browser.
//
// The port only ever creates and reads events. There is no update and no
// delete, here or on the server, and that is what makes "append-only" a
// property of the system rather than a convention the client observes.

import type { ProgressEvent } from "@aybuben/core/progress/event.ts";
import type { GameState } from "@aybuben/core/state.ts";

/**
 * The remote schema version. Born at 6, alongside local.ts — F is the
 * last free moment to choose the persisted representation, because nobody has
 * data yet.
 */
export const REMOTE_VERSION = 6;

/** Rows per pull. Bounded so a cold start with a long log pages rather than stalls. */
export const PAGE = 100;

export interface RemoteSnapshot {
	version: number;
	/** The content version this was folded under. */
	content: string;
	/**
	 * The event row id this snapshot folds through — NOT a timestamp.
	 *
	 * Two rows can share a `$createdAt` to the millisecond, and a value cursor
	 * would then skip the losing row forever. `Query.cursorAfter(rowId)` over
	 * `orderAsc($createdAt)` is tie-safe, and a row id is usable as a durable
	 * watermark precisely because an event row is never deleted.
	 *
	 * Ordering is untouched: a row pushed after a week offline gets a
	 * fresh `$createdAt` above the cursor row's and so sorts after it. It may
	 * sort *earlier* than events already folded, which re-orders the result —
	 * safe, because every snapshot is a cache.
	 */
	coversUpTo: string | null;
	state: GameState;
}

export interface EventPage {
	events: ProgressEvent[];
	/** The last row id in this page, or the cursor passed in when it was empty. */
	cursor: string | null;
	more: boolean;
}

export type RemoteErrorKind = "offline" | "server" | "cursor" | "unauthorized" | "unknown";

export class RemoteError extends Error {
	readonly kind: RemoteErrorKind;
	constructor(kind: RemoteErrorKind, message: string = kind) {
		super(message);
		this.name = "RemoteError";
		this.kind = kind;
	}
}

export interface RemoteProgress {
	/** The compacted snapshot, or null if this account has never had one. */
	pullSnapshot(userId: string): Promise<RemoteSnapshot | null>;
	/** Events after the given row id, oldest first. `after: null` starts at the beginning. */
	pullEvents(userId: string, after: string | null, limit: number): Promise<EventPage>;
	/** Append one event. The row id is the event id, so a duplicate is a success. */
	push(userId: string, e: ProgressEvent): Promise<void>;
	putSnapshot(userId: string, s: RemoteSnapshot): Promise<void>;
}
